import { generateObjectId } from '@alpod-ai-writer/shared';
import { Button, TextInput } from 'flowbite-react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import * as Y from 'yjs';

const OTHER_ATT_PREFIX = 'oth_att_items_';
const OTHER_ATT_TITLE_PREFIX = 'oth_att_title_';

export function useOtherAttachmentItems(yDoc: Y.Doc, contentId: string) {
    const arr = yDoc.getArray<Y.Map<string>>(OTHER_ATT_PREFIX + contentId);

    const [itemIds, setItemIds] = useState<string[]>(() =>
        arr.toArray().map((m) => m.get('id') ?? '')
    );

    useEffect(() => {
        const handler = () => setItemIds(arr.toArray().map((m) => m.get('id') ?? ''));
        arr.observe(handler);
        return () => arr.unobserve(handler);
    }, [arr]);

    const addItem = useCallback(() => {
        const id = generateObjectId();
        const map = new Y.Map<string>();
        map.set('id', id);
        arr.push([map]);
    }, [arr]);

    const removeItem = useCallback((id: string) => {
        const index = arr.toArray().findIndex((m) => m.get('id') === id);
        if (index !== -1) arr.delete(index, 1);
    }, [arr]);

    return { itemIds, addItem, removeItem };
}

export const OtherAttachmentsAddButton: React.FC<{ yDoc: Y.Doc; contentId: string }> = ({ yDoc, contentId }) => {
    const { addItem } = useOtherAttachmentItems(yDoc, contentId);
    return (
        <Button color='primary' size='sm' onClick={addItem}>
            <PlusIcon className='h-4 w-4 mr-1.5' />
            Pridať prílohu
        </Button>
    );
};

function OtherAttachmentTitle({
    yDoc,
    itemId,
    index,
    readOnly,
    onRemove,
}: {
    yDoc: Y.Doc;
    itemId: string;
    index: number;
    readOnly: boolean;
    onRemove: () => void;
}) {
    const yText = yDoc.getText(OTHER_ATT_TITLE_PREFIX + itemId);
    const [value, setValue] = useState(() => yText.toString());

    useEffect(() => {
        const handler = () => setValue(yText.toString());
        yText.observe(handler);
        return () => yText.unobserve(handler);
    }, [yText]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        yDoc.transact(() => {
            yText.delete(0, yText.length);
            yText.insert(0, newVal);
        });
    }, [yDoc, yText]);

    return (
        <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-400 font-mono w-6 shrink-0'>{index + 1}.</span>
            <TextInput
                value={value}
                onChange={handleChange}
                placeholder='Název přílohy'
                disabled={readOnly}
                sizing='sm'
                className='flex-1 font-medium'
            />
            {!readOnly && (
                <button
                    className='p-1.5 text-gray-300 hover:text-red-500 rounded-md transition-colors shrink-0'
                    onClick={onRemove}
                    title='Odstrániť prílohu'
                >
                    <TrashIcon className='h-4 w-4' />
                </button>
            )}
        </div>
    );
}

export const OtherAttachmentsEditor: React.FC<{
    yDoc: Y.Doc;
    contentId: string;
    readOnly?: boolean;
}> = ({ yDoc, contentId, readOnly = false }) => {
    const { itemIds, removeItem } = useOtherAttachmentItems(yDoc, contentId);

    if (itemIds.length === 0) {
        return (
            <p className='px-4 md:px-8 text-sm text-gray-500 italic'>
                {readOnly ? 'Žiadne ostatní prílohy.' : 'Žiadne ostatní prílohy. Pridajte prvú pomocou tlačidla vyššie.'}
            </p>
        );
    }

    return (
        <div className='flex flex-col gap-2 px-4 md:px-8'>
            {itemIds.map((id, idx) => (
                <OtherAttachmentTitle
                    key={id}
                    yDoc={yDoc}
                    itemId={id}
                    index={idx}
                    readOnly={readOnly}
                    onRemove={() => removeItem(id)}
                />
            ))}
        </div>
    );
};

export function getOtherAttachmentTitles(yDoc: Y.Doc, contentId: string): string[] {
    const arr = yDoc.getArray<Y.Map<string>>(OTHER_ATT_PREFIX + contentId);
    return arr.toArray().map((m) => {
        const id = m.get('id') ?? '';
        return yDoc.getText(OTHER_ATT_TITLE_PREFIX + id).toString();
    });
}
